import type {StringIDFolder} from 'constants/FolderConstants';
import type {Comment, FromServerProtocol, FromServerProtocolVersion} from 'notebook/ELNConstants';
import type {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import type {CidIdMap} from 'notebook/notes/NoteConstants';
import type {ProtocolStoreTransformsProtocol} from 'notebook/protocols/ProtocolStoreTransforms';
import type {Timer} from 'notebook/protocols/timers/TimerTypes';
import type {Response} from 'notifications';

export const PROTOCOL_EVENT_PAYLOADS = {
  PROTOCOL_OPEN: {} as {protocolID: string},
  PROTOCOL_OPEN_COMPLETED: {} as {
    protocolID: string;
    protocol: FromServerProtocol;
  },
  PROTOCOL_OPEN_FAILED: {} as {
    protocolID: string;
    error: Response;
  },

  PROTOCOL_UPDATE: {} as {
    protocolID: string;
    data: {tagSchemaId: string | null};
  },
  PROTOCOL_UPDATE_COMPLETED: {} as {
    protocolID: string;
    data: {tagSchemaId: string | null};
    serverData: {editURL?: string; folder?: StringIDFolder};
  },
  PROTOCOL_UPDATE_FAILED: {} as {
    protocolID: string;
    error: Response;
  },

  PROTOCOL_DELETE: {} as {protocolID: string},
  PROTOCOL_DELETE_COMPLETED: {} as {protocolID: string},
  PROTOCOL_DELETE_FAILED: {} as {
    protocolID: string;
    error: Response;
  },

  PROTOCOL_COMMENT: {} as {
    protocolID: string;
    linkableID: string;
    linkType: string;
    onDescription: boolean;
    text: string;
  },
  PROTOCOL_COMMENT_COMPLETED: {} as {
    protocolID: string;
    comment: Comment;
  },
  PROTOCOL_COMMENT_FAILED: {} as {
    protocolID: string;
    error: Response;
  },
  PROTOCOL_DELETE_COMMENT: {} as {
    protocolID: string;
    commentID: number;
  },
  PROTOCOL_DELETE_COMMENT_COMPLETED: {} as {
    protocolID: string;
    commentID: number;
  },
  PROTOCOL_DELETE_COMMENT_FAILED: {} as {
    protocolID: string;
    commentID: number;
    error: Response;
  },

  PROTOCOL_STEP_COMPLETE_COMPLETED: {} as {
    protocolID: string;
    stepID: number;
  },
  PROTOCOL_STEP_COMPLETE_FAILED: {} as {
    protocolID: string;
    stepID: number;
  },
  PROTOCOL_STEP_RESET_COMPLETED: {} as {
    protocolID: string;
    stepID: number;
  },
  PROTOCOL_STEP_RESET_FAILED: {} as {
    protocolID: string;
    stepID: number;
  },

  PROTOCOL_RESET_STEPS_COMPLETED: {} as {protocolID: string},
  PROTOCOL_RESET_STEPS_FAILED: {} as {protocolID: string},

  EDITOR_UPDATE: {} as {
    protocolID: string;
    op: unknown;
    cidIdMap: CidIdMap;
    bumpVersion: boolean;
  },
  EDITOR_REPLACE: {} as {
    protocolID: string;
    protocol: ProtocolStoreTransformsProtocol;
    restoreData: SelectionRestoreData;
  },
  EDITOR_CLOSE: {} as {protocolID: string},
  EDITOR_FETCH_VERSIONS: {} as {protocolID: string},
  EDITOR_FETCH_VERSIONS_COMPLETED: {} as {
    protocolID: string;
    versions: Array<FromServerProtocolVersion>;
  },
  EDITOR_FETCH_VERSIONS_FAILED: {} as {protocolID: string},
  EDITOR_LOAD_VERSION: {} as {
    protocolID: string;
    versionId: number;
  },
  EDITOR_LOAD_VERSION_COMPLETED: {} as {
    protocolID: string;
    versionId: number;
    version: Array<FromServerProtocolVersion>;
  },
  EDITOR_LOAD_VERSION_FAILED: {} as {
    protocolID: string;
    versionId: number;
  },
  EDITOR_REGISTER_OBJECTS: {} as {
    protocolID: string;
    cidIdMap: CidIdMap;
  },
  EDITOR_RESET_TO_OPEN_VERSION: {} as {protocolID: string},
  EDITOR_SAVE: {} as {protocolID: string},
  EDITOR_SAVE_COMPLETED: {} as {
    protocolID: string;
    savedVersion: number;
    versionId: FromServerProtocol['versionId'];
    cidIdMap: CidIdMap;
    serverData: {editURL?: string};
  },
  EDITOR_SAVE_FAILED: {} as {
    protocolID: string;
    error: Response;
  },

  EDITOR_OPEN: {} as {protocolID: string},
  EDITOR_OPEN_COMPLETED: {} as {
    protocolID: string;
    protocol: FromServerProtocol;
  },
  EDITOR_OPEN_FAILED: {} as {
    protocolID: string;
    error: Response;
  },

  TIMER_UPDATE: {} as {
    timerId: string;
    options: Partial<Timer>;
  },
  TIMER_REMOVE: {} as {
    timerId: string;
  },
} as const;

export type ProtocolEventPayload = typeof PROTOCOL_EVENT_PAYLOADS;
